import React, { useEffect, useContext, useState, useRef, useLayoutEffect } from 'react'
import { Box, Switch } from '@mui/material'

import LanguageContext from "../../../contexts/LanguageContext"
import UserContext from "../../../contexts/UserContext"

import SuperAdminContext from "../../../contexts/SuperAdminContext"


import { TopBar, BottomBar, CenterContainer, BlocIllustre, ImageCarree, useRaccourciClavier, saisieEnCours, LARGEUR_CONTENU, LARGEUR_QUESTION, LARGEUR_CARTE_TEXTE, LARGEUR_CARTE_ILLUSTREE, LARGEUR_CARTES, COLONNE_ILLUSTREE, LARGEUR_LECTURE, BLOC_DEFILANT, sansBlancsAutour, IMAGE_PLEINE, imagePleine, coteDeLImage, DEGRADE_PANNEAU, useApercuPleineHauteur , FOND_BANDE_BASSE, ROGNE_HAUT, retraitPlanche, HAUTEUR_PLANCHE, useSuiteCachee, FonduBas, FOND_ECRAN_CONTENU } from "./FormatsHelpers"
import { APERCU_EDITO, marquerChapo } from "./apercu"

import EditableField from "./EditableField"
import { avecPuces } from "./puces"
import EditableButton from "./EditableButton"

import EditorPopover from "../../ui/Admin/Formations/EditorPopover"

const FormatTexte = ({ actFormation, actIndex, format, setViewPlanCours, viewPlanCours, onlyContent, isEditor, sequenceSubMenu, setSequenceSubMenu }) => {

  const { actLanguage, languageValues } = useContext(LanguageContext)

  /* APERCU EDITORIAL : le premier bloc du texte devient un chapo, mais
     seulement s'il est assez court. On laisse le NAVIGATEUR mesurer, jamais une
     expression reguliere sur le HTML — voir le commentaire dans `apercu.js`.
     `useLayoutEffect` et non `useEffect` : la marque doit etre posee AVANT la
     peinture, sinon le texte s'affiche une image en petit puis saute en grand. */
  const refTexte = useRef(null)
  useLayoutEffect(() => {
    if (APERCU_EDITO) marquerChapo(refTexte.current)
  }, [format?.id, format?.text])

  const { manageFormatResponse, manageSlide } = useContext(UserContext)

  let superAdminContext = null
  if (isEditor) {
    superAdminContext = useContext(SuperAdminContext)
  }


  /* ⚠️ LES DEUX MODES. 114 ecrans du catalogue portent une image en `contain` :
     schemas, logos, visuels qui ne doivent PAS etre rognes. Une image pleine
     hauteur les decouperait. Ceux-la gardent donc le cadre carre, comme
     aujourd'hui ; seules les images `cover`, faites pour remplir, passent en
     pleine hauteur. L'ancien lecteur procedait deja ainsi, avec un second
     masque. */
  const pleineHauteur = useApercuPleineHauteur() && format?.image?.type !== "contain"
  /* Le cote de l'image alterne d'un ecran a l'autre, regulierement. */
  /* L'alternance n'a de sens que s'il y a une image a faire alterner. Sans
     image, on garde l'ancrage a gauche, toujours le meme. */
  /* ⛔ 01/10/2026 — UN SEUL PREDICAT POUR « Y A-T-IL UNE IMAGE ? », et c'est
     l'ADRESSE. `FormatQuizz` l'a appris le 21/09 et le documente : l'editeur
     ecrit `{link:"", type:"cover"}` des que l'admin VIDE le champ d'URL, donc
     l'objet existe alors que l'image n'existe pas. Ce fichier testait encore
     l'objet. Avec les deux moities (30/09), l'ecart coutait plus cher qu'avant :
     l'ecran passait pour illustre, le contenu partait dans la moitie droite et
     la moitie gauche restait VIDE.
     Releve en base locale le 01/10 : 0 ecran dans cet etat aujourd'hui, donc
     rien ne change a l'ecran. C'est un piege arme, pas un defaut actif — il se
     declencherait le jour ou Nawal vide une adresse.
     ⚠️ Les interrupteurs de l'EDITEUR gardent `format?.image` : eux demandent
     bien « ce format a-t-il un bloc image ? », pas « y a-t-il une image ». */
  const aUneImage = !!format?.image?.link
  const cote = !pleineHauteur ? null : (aUneImage ? coteDeLImage(actIndex) : "droite")

  /* ⛔ UN ECRAN SANS IMAGE GARDE LA MEME MISE EN PAGE (09/09/2026).
     Sans cela le titre saute de 311 px en moyenne d'un ecran a l'autre et
     change d'alignement — mesure au banc, cf. DEGRADE_PANNEAU. La moitie
     illustree recoit le panneau de l'ancien lecteur : l'ecran reste habite,
     et surtout le texte ne bouge plus.
     `commeIllustre` est la seule notion utilisee plus bas : un ecran est
     traite comme illustre s'il porte une image OU s'il recoit le panneau.
     Hors apercu, `panneau` est faux et rien ne change. */
  const panneau = pleineHauteur && !aUneImage
  const commeIllustre = aUneImage || panneau

  /* ⛔ 06/10/2026 — LE FONDU DU TEXTE LONG VIT DANS LA BOITE DE TEXTE.
     Historique : le point 1.1 de la review du 5 oct posait le fondu de la
     planche 06' sur les 70 px au-dessus de la barre du bas, c'est-a-dire sur
     l'ECRAN, et seulement pour un ecran illustre. Nawal ne le voyait pas sur
     LAB : ancre a l'ecran, il recouvrait le bord bas de la carte et son
     dernier blanc plutot que le texte, et il n'existait ni sans image ni dans
     l'apercu de l'editeur.
     Consigne de Nawal (06/10) : un leger fondu au bas de la BOITE, de la
     couleur de la boite, seulement s'il reste du texte dessous, qui disparait
     quand on arrive en bas ; jamais sur toute la page ; le design de la boite
     ne change pas ; meme regle pour toutes les variantes du format Texte.
     Mecanisme partage : `useSuiteCachee` (la carte OU la page defile) et
     `FonduBas collant` (helpers). La vignette de l'editeur (`onlyContent`)
     n'en a pas : elle ne defile pas. */
  const [refCarte, suite] = useSuiteCachee()
  const fonduVisible = onlyContent !== true && suite.cachee

  /* ⛔ 01/10/2026 — « ↵ POUR CONTINUER » (point 3 d'Hicham). Libelle releve mot
     pour mot sur la planche 06, node 3:141180, a x=381 face au CTA de x=981
     (node 3:141184). Le geste est sans ambiguite ici : l'ecran ne porte aucun champ, la
     touche Entree n'a aucun autre emploi, et le bouton est toujours actif. */
  const continuer = () => {
    manageFormatResponse(format, [{ key: 'isDone', value: true }])
    manageSlide(1)
  }
  const reperesClavier = [{ touche:"entree", libelle: languageValues?.formation?.toucheContinuer?.[actLanguage] }]
  useRaccourciClavier(isEditor !== true && onlyContent !== true, (e) => {
    if (e.key !== "Enter" || saisieEnCours(e)) return
    e.preventDefault()
    continuer()
  })

  /* ⛔ PLUS DE PANNEAU SUR LES ECRANS SANS IMAGE (Hicham, 09/09 : « c'est
     moche quand il y a que du texte »). Le degrade bleu nuit occupait la
     moitie de l'ecran en face d'une colonne de 520 px : sur un ecran qui ne
     porte QUE du texte, cela faisait une page a moitie vide et a moitie
     sombre. Il venait pourtant de l'ancien lecteur — mais chez lui il
     PORTAIT les propositions du quiz ; vide, il ne raconte rien.
     Ce qu'on garde, c'est l'ANCRAGE : le texte reste au meme endroit que sur
     les ecrans illustres, ce qui etait tout l'objet de la regle. Ce qu'on
     rend, c'est la LARGEUR : la colonne passe a la largeur de lecture. */
  const imageDuFormat = !aUneImage ? null
    : pleineHauteur
      ? <Box sx={{ ...imagePleine(cote), backgroundImage:"url(\""+format?.image?.link+"\")", backgroundSize:"cover" }} />
      : <ImageCarree lien={format?.image?.link} type={format?.image?.type} bordABord />

  return (
    <Box sx={{
      display:"flex",
      flexDirection:"column",
      /* ⚠️ `100vh` vaut la hauteur de la FENETRE, jamais celle d'un conteneur.
         Un apercu qui simule 982 px dans un panneau aurait donc affiche un ecran
         calibre sur la vraie fenetre. La variable `--hs-hauteur-ecran` n'est
         definie QUE par le panneau d'apercu ; partout ailleurs le repli `100vh`
         s'applique et rien ne change. Une variable CSS plutot qu'un prop : neuf
         signatures de composants restent intactes, et un format rendu hors du
         lecteur se comporte correctement sans qu'on ait pense a le lui dire. */
      height:onlyContent?"100%":"var(--hs-hauteur-ecran, 100vh)",
      /* ⛔ LE FORMAT DEVIENT SON PROPRE REFERENTIEL DE MESURE (08/09/2026).
         Nawal voit « de grosses differences » entre l'apercu de l'admin et la
         vue apprenant. Cause : l'apercu ne rend pas l'ecran a la taille de la
         fenetre. `ScaleThumbForm` le rend dans une boite FIXE de 1500 x 1000,
         puis la reduit par un `transform: scale()`. Or `vw` et `vh` se
         calculent sur la FENETRE, jamais sur cette boite : les proportions
         divergeaient donc des deux cotes, sans qu'aucun reglage puisse les
         reconcilier — ce sont deux referentiels differents.
         En declarant ce conteneur, les unites `cqw`/`cqh` employees plus bas se
         mesurent sur LUI. Dans le lecteur il occupe la fenetre : le rendu ne
         bouge pas d'un pixel. Dans l'apercu il fait 1500 x 1000 : le rendu
         devient fidele. */
      containerType:"size",
      overflow:"hidden",
      /* ancre de l'image pleine hauteur en apercu editorial */
      position: APERCU_EDITO ? "relative" : "static",
      /* ⛔ Pas de gap racine : la planche colle le contenu au bandeau. */
    }}>
      { /* EDITOR ADMIN */}
      {isEditor &&
        <EditorPopover
          open={sequenceSubMenu}
        >
          <Box sx={{marginBottom:"2px", fontWeight:"bold"}}>Image</Box>
          <Box sx={{border:"1px solid #2929292f", padding:"5px"}}>
            <Box sx={{display:"flex", gap:"5px", alignItems:"center"}}>
              <Switch
                sx={{marginLeft:"-5px"}}
                checked={format?.image?true:false}
                onChange={(e) => {
                  if (e.target.checked) {
                    superAdminContext?.onSequenceChange(
                      'image',
                      { link: 'https://video.thelab.risinguparis.com/brain.svg', type: 'contain' },
                      superAdminContext?.adminPlayerIndexes
                    )
                  } else {
                    superAdminContext?.onSequenceChange(
                      'image',
                      null,
                      superAdminContext?.adminPlayerIndexes
                    )
                  }
                }}
                size="small"
                color="warning"
              />
              Afficher une image
            </Box>
            {format?.image &&
              <>
                {/* ⛔ 01/10/2026 — L'INTERRUPTEUR « ELARGIR L'IMAGE » EST RETIRE.
                    Decision d'Hicham : « Image entiere toujours », puis « oui
                    retire, on verra avec Nawal ce qu'elle prefere ».
                    Il choisissait entre `cover` (remplir, donc rogner) et
                    `contain` (tout voir). L'image est desormais TOUJOURS
                    entiere : il ne decidait plus de rien. Un interrupteur qui ne
                    fait rien est pire qu'un interrupteur absent — Nawal l'aurait
                    actionne en cherchant un effet.
                    ⚠️ LA DONNEE N'EST PAS TOUCHEE : `image.type` reste en base
                    sur les ecrans existants, simplement plus lu pour le
                    rognage. Si la decision revient, c'est ce bloc qu'on remet,
                    et rien a remigrer. */}
                <Box sx={{display:"flex", gap:"5px", alignItems:"center"}}>
                  <Box>
                    Url&nbsp;:
                  </Box>
                  <Box sx={{flex:1}}>
                    <input
                      value={format?.image?.link}
                      style={{width:"100%"}}
                      onChange={(e) => {
                        superAdminContext?.onSequenceChange(
                          'image',
                          { link: e.target.value, type: format?.image?.type },
                          superAdminContext?.adminPlayerIndexes
                        )
                      }}
                    />
                  </Box>
                </Box>
              </>
            }
          </Box>
        </EditorPopover>
      }
      { /* END EDITOR ADMIN */}

      <TopBar
        actFormation={actFormation}
        actIndex={actIndex}
        setViewPlanCours={setViewPlanCours}
        viewPlanCours={viewPlanCours}
        onlyContent={onlyContent}
      />
      <CenterContainer
        actFormation={actFormation}
        actIndex={actIndex}
        onlyContent={onlyContent}
        cote={cote}
              pastilleDansColonne
              pastilleAvecImage={aUneImage}
              /* 40 px sous la carte en Tablet empile (3:181437, `pb-40`) : pas de
                 bande collee en bas sur cet ecran. */
              basEmpile={40}
              /* Mobile : la planche 06 pose la pastille a 140 sous la barre
                 d'etat (3:141238), comme la 02 : 80 px de retrait. */
              /* B87 (04/10/2026) : la valeur de la planche a la hauteur de la
                 planche seulement, comme le quiz (PD-23) — a 800 x 360, 160 px
                 fixes poussaient le titre sous la bande du bas */
              retraitHaut={commeIllustre ? undefined : [
                retraitPlanche(24, 80, HAUTEUR_PLANCHE.mobile),
                retraitPlanche(32, 160, HAUTEUR_PLANCHE.tablette),
                retraitPlanche(48, 120, HAUTEUR_PLANCHE.desktop),
                retraitPlanche(48, 120, HAUTEUR_PLANCHE.desktop),
              ]}
      >
        {({ pastille }) => (<>
        {/* PROPOSITION EN COURS D'ARBITRAGE (Nawal, 08/09). Sans image, le
            titre reste au-dessus et centre, comme aujourd'hui. AVEC image, il
            descend dans la colonne de droite et se ferre a gauche, exactement
            comme la question d'un quiz : les deux ecrans deviennent alors
            structurellement identiques, et le bloc commence a la meme hauteur.
            Mesure avant : 108 px d'ecart vertical et deux alignements
            differents pour le meme role. */}
        {!commeIllustre &&
          <>
            {/* ⛔ SANS IMAGE, LA PASTILLE PRECEDE LE TITRE (planche 06, Tablet
                3:141225 et Mobile 3:141238, constate par le comparateur le
                02/10/2026). La pastille de colonne, seule visible aux paliers
                empiles, etait posee par BlocIllustre, donc APRES ce titre : elle
                passait sous la question. Aux grands paliers elle reste cachee et
                celle de la page s'affiche, comme avant. */}
            {pastille}
            <EditableField
              field={sansBlancsAutour(format?.title)}
              type="title"
              isEditor={isEditor}
              sx={{
                color:"text.black",
                /* 20 px entre pastille et titre (Tablet 3:141225 -> 3:141230,
                   Mobile 3:141238 -> 3:141243) ; la pastille de colonne en pose
                   deja 4 en Mobile. Aux grands paliers, c'est celle de la page. */
                marginTop:["16px", "20px", 0, 0],
                /* ⚠️ ECHELLE DU TITRE RELUE DANS FIGMA (21/09/2026, lot 3a).
                   Jeton nomme « Text/Desktop/Heading/H1 » : Questrial Regular,
                   42 px, interligne 1,15, letterSpacing 0. Noeuds 3:302257
                   (Desktop), 3:282076 (Tablet), 3:282135 (Mobile, 26 px / 1,25).
                   Le 46 px du palier lg etait estime : la planche s'arrete a 42.
                   L'interligne n'etait declare NULLE PART, donc le navigateur
                   retombait sur `normal`, soit environ 1,15 a 1,2 selon la
                   police. On le pose explicitement. */
                fontSize:["26px", "42px", "42px", "42px"],
                lineHeight:["1.25", "1.15", "1.15", "1.15"],
                ...ROGNE_HAUT,  /* haut des capitales, comme la maquette : voir FormatsHelpers */
                /* Et le BAS a la ligne de base (planche 06, 3:141173, 02/10/2026) : sinon
                   l'ecart question / carte prenait la descente de la derniere
                   ligne en plus (+12 px Desktop). Le texte deborde de sa boite
                   vers le bas, sans etre coupe : rien ne le borne ici. */
                textBox: "trim-both cap alphabetic",
                textAlign:"center",
                /* La question tient dans 1000 px, comme sur la planche (3:141173) ;
                   en pleine largeur (1432) elle coupait ses lignes ailleurs. */
                maxWidth: LARGEUR_QUESTION, width:"100%", marginLeft:"auto", marginRight:"auto",
              }}
            />
            <EditableField
              field={format?.subTitle}
              type="subTitle"
              isEditor={isEditor}
              sx={{
                color:"text.subTitle",
                /* ⚠️ CONSIGNE RELUE DANS FIGMA (21/09/2026, lot 3a). Jeton
                   « Text/Desktop/Body/Large » : 20 px, interligne 1,5, aux deux
                   grands paliers ; 15 px en Mobile (3:282136). Cinq formats
                   portaient cinq echelles differentes pour le MEME element. */
                  fontSize:["15px", "20px", "20px", "20px"],
                  lineHeight:"1.5",
                textAlign:"center",
                /* Tablet 20 et non 15 (planche 06, 02/10/2026) : la consigne vide
                   met ses deux marges entre question et carte, 40 px comme en
                   Desktop (3:141230 -> 3:141231). */
                margin:["10px 0px", "20px 0px", "15px 0px", "20px 0px"]
              }}
            />
          </>
        }
        {/* Points 9 et 13 : la regle des blocs est desormais commune a tous les
            formats illustres (BlocIllustre / ImageCarree dans FormatsHelpers).
            Sans image, le texte est borne a la largeur de lecture. */}
        <BlocIllustre
          pastille={commeIllustre ? pastille : null}
          image={imageDuFormat}
          horsFlux={cote}
        >
          <Box sx={{
            width: commeIllustre ? LARGEUR_CONTENU : "100%",
            /* ⚠️ LA COLONNE PORTE LE TITRE : elle suit donc la largeur d'enonce
               de la planche (1000), et non l'ancien 720 qui n'a aucune
               contrepartie Figma. La carte, elle, se plafonne plus bas. */
            maxWidth: commeIllustre ? "100%" : LARGEUR_QUESTION,
            display:"flex",
            flexDirection:"column",
            ...(commeIllustre ? COLONNE_ILLUSTREE : {}),
            /* la moitie gauche, moins la marge du conteneur et une gouttiere */
            /* ⚠️ DEUX LARGEURS. Face a une image, la colonne tient dans sa moitie
               (520 px). Sans image, elle n'a plus de voisine : la maintenir a
               520 px laissait un ecran a moitie vide, ce qu'Hicham a trouve
               moche « quand il y a que du texte ». Elle passe alors a la
               largeur de lecture, en gardant le meme bord gauche : le texte ne
               bouge pas, il respire. */
            ...(pleineHauteur ? { width: format?.image ? ["100%", "100%", "min(38cqw, 520px)", "min(38cqw, 520px)"] : ["100%", "100%", "min(52cqw, 720px)", "min(52cqw, 720px)"], maxWidth:"100%",
                                  position:"relative", zIndex:1 } : {}),
          }}>
          {commeIllustre &&
            <>
              <EditableField
                field={sansBlancsAutour(format?.title)}
                type="title"
                isEditor={isEditor}
                sx={{
                  color:"text.black",
                  fontSize: APERCU_EDITO ? ["30px", "46px", "46px", "54px"] : ["26px", "42px", "42px", "46px"],
                  lineHeight: APERCU_EDITO ? "106%" : undefined,
                  textAlign:"center", // Lot B : titre centre aussi en desktop (frames image « Claude »)
                }}
              />
              <EditableField
                field={format?.subTitle}
                type="subTitle"
                isEditor={isEditor}
                sx={{
                  color:"text.subTitle",
                  /* ⚠️ CONSIGNE RELUE DANS FIGMA (21/09/2026, lot 3a). Jeton
                   « Text/Desktop/Body/Large » : 20 px, interligne 1,5, aux deux
                   grands paliers ; 15 px en Mobile (3:282136). Cinq formats
                   portaient cinq echelles differentes pour le MEME element. */
                  fontSize:["15px", "20px", "20px", "20px"],
                  lineHeight:"1.5",
                  textAlign:"center", // Lot B : sous-titre centre aussi en desktop
                  marginTop:["6px", "8px", "8px", "10px"],
                }}
              />
            </>
          }
          <Box ref={refCarte} sx={{
            width:"100%",
            /* ⚠️ LA CARTE EST PLUS ETROITE QUE SA COLONNE (21/09/2026, lot 2).
               Figma : carte 600 dans une colonne de 1000 quand le texte est
               seul, carte 700 dans une colonne de 741 quand il est illustre.
               Dans les deux cas elle est CENTREE, d'ou la marge automatique.
               Aux paliers empiles (xs, sm) elle occupe toute la largeur utile,
               deja fixee par les gouttieres du lot 1. */
            /* ⛔ 02/10/2026 (comparateur, planche 06) : sans image, la carte fait
               aussi 600 en Tablet (3:141231), pas toute la largeur. */
            maxWidth: ["100%", commeIllustre ? "100%" : LARGEUR_CARTE_TEXTE,
                       commeIllustre ? LARGEUR_CARTE_ILLUSTREE : LARGEUR_CARTE_TEXTE,
                       commeIllustre ? LARGEUR_CARTE_ILLUSTREE : LARGEUR_CARTE_TEXTE],
            marginLeft:"auto",
            marginRight:"auto",
            marginTop: format?.image ? ["14px", "18px", "18px", "22px"] : 0,
            /* ⛔ 04/10/2026 — EMPILEE, LA CARTE SUIT SON TEXTE. En pile (Mobile,
               Tablet), `flex: 1` l'etirait jusqu'au bas de l'ecran, vide (1024 x
               640 : 341 px de carte pour 200 de texte ; relecture du 04/10) ;
               la planche 06' Tablet la borne a son texte (3:181444). */
            /* ⛔ 06/10/2026 — EN DEUX COLONNES AUSSI (review de Nawal, point 1.1).
               `flex: 1` l'etirait jusqu'a « Suivant », vide sous un texte court.
               La planche 06' pose la carte a hauteur de son texte, bord haut fixe
               (3:181382). `0 1 auto` : elle suit son texte, et ne cede (puis
               defile) que si le texte depasse la place disponible. */
            flex: format?.image ? ["0 0 auto", "0 0 auto", "0 1 auto", "0 1 auto"] : "unset",
            minHeight:0,
            /* Carte de contenu — fond BLANC, bordure fine #E6E6E7 et ombre
               douce, au lieu de l'ancien cadre #FAF8F7 / #D4D4D4. On ne
               conserve pas l'ancien style : Figma est la verite visuelle.
               ⛔ 01/10/2026 — LE NUMERO A CHANGE, PAS LES VALEURS. Il citait
               `8545-26436`, qui n'existe QUE dans le fichier d'ARCHIVE. Le bon
               noeud du fichier « Claude » est `3:181382` (planche 06', carte de
               700 a x=751,5). Relu par l'API le 01/10 : fond #FFFFFF, bordure
               #E6E6E7 de 1 px, ombre interieure flou 12 et ombre portee flou 15,
               toutes deux noir a 5 %, marges interieures de 25 px.
               Les valeurs du code etaient donc JUSTES — seule leur source
               renvoyait vers la mauvaise maquette.
               (La branche APERCU_EDITO, NON promue pour ce format, pose au
               contraire le texte a meme le fond ; elle reste derriere le flag.) */
            background: APERCU_EDITO ? "transparent" : "#FFFFFF",
            border: APERCU_EDITO ? "none" : "1px solid #E6E6E7",
            /* ⚠️ CORRECTION 21/09/2026 — lu dans Figma, node 3:181382 (planche 06',
               Desktop/Default) : `0px 0px 7.5px rgba(0,0,0,0.05)`. C'est un HALO
               diffus, sans decalage. L'ancienne valeur `0 1px 4px` etait estimee a
               l'oeil sur l'export : decalee vers le bas et deux fois moins etalee,
               elle se lisait comme un simple trait gris. Hicham : « le contour n'est
               pas le meme, il y a de l'ombre aussi ».
               ✅ La BORDURE, elle, etait juste : Figma donne bien `1px solid #E6E6E7`. */
            /* ⛔ 03-04/10/2026 — 15 px, PAS 7,5. L'API le dit sur la planche 06
               (3:141174 : ombre portee, flou 15, noir a 5 %, relu par le
               comparateur) ET sur la planche 06' (3:181382, bloc du 01/10
               ci-dessus). Les 7,5 du 21/09 venaient d'une autre lecture de
               3:181382, contredite par l'API. L'ombre interieure de flou 12
               des planches est desactivee dans Figma (`visible: false`). */
            boxShadow: APERCU_EDITO ? "none" : "0 0 15px rgba(0,0,0,0.05)",
            /* ⚠️ Figma donne 25px UNIFORME sur Desktop (node 3:181382), pas
               « 30 en haut, 15 sur les cotes ». Les paliers empiles gardent des
               valeurs plus serrees, faute de node Desktop qui les decrive. */
            /* Sans image (planche 06) : 25 px partout, 20 sur les cotes en Mobile
               (3:141244). Avec image, les valeurs d'avant, faute de planche. */
            /* ⚠️ Figma mesure le texte du HAUT DES CAPITALES a la LIGNE DE BASE
               (text-box-trim) ; le navigateur compte toute la hauteur de ligne.
               Pour 20 px / 30 : 7,8 px de plus en haut d'un paragraphe, 9,2 en
               bas ; pour 15 / 21 : 5,1 et 5,9. On les retire des marges plutot
               que de rogner le texte, ce qui couperait les accents de la
               premiere ligne dans une carte qui defile. Resultat a l'oeil :
               les 25 px de la planche (3:141174, 3:141244), partout. */
            padding: APERCU_EDITO ? 0 : (commeIllustre ? ["15px 10px", "20px 13px", "25px", "25px"] : ["20px 20px 19px", "17px 25px 16px", "17px 25px 16px", "17px 25px 16px"]),
            /* Point 16 : le bloc garde sa hauteur et le contenu defile a
               l'interieur, barre visible, plutot que de pousser le bandeau. */
            ...BLOC_DEFILANT,
            boxSizing:"border-box",
            display:"flex",
            flexDirection:"column",
            /* repere du fondu quand c'est la page qui defile (`FonduBas`),
               pose seulement alors : sans effet visible, et le reste du temps
               la carte est rendue exactement comme avant */
            ...(fonduVisible && suite.mode === "page" ? { position:"relative" } : {}),
          }}>
            {/* Releve sur la maquette « Format texte + image » : le texte est
                centre VERTICALEMENT dans son cadre. Sans ca, une phrase courte
                flotte en haut d'un bloc aussi haut que l'image carree, avec un
                grand vide dessous — c'est exactement ce qu'on voyait.
                ⚠️ `margin: auto 0` et non `justifyContent: center` : dans un
                bloc qui peut defiler, le centrage par flex rogne le HAUT du
                contenu des qu'il deborde, alors que les marges automatiques se
                resorbent d'elles-memes. Rien a voir avec le point 6, qui
                concernait le conteneur de PAGE et non l'interieur d'un cadre. */}
            {/* ⛔ LE TEXTE SE CALE EN HAUT (capture d'Hicham, 22/09/2026).
                `margin: "auto 0"` le CENTRAIT verticalement dans la carte. Sur
                la planche, la carte EPOUSE son contenu — node 3:181382, 700 x
                921, pour un texte de 869 plus 2 x 26 de marge : elle est pleine.
                Dans le code la carte s'etire pour remplir la colonne (flex:1),
                et un texte court se retrouvait donc pousse au MILIEU d'un cadre
                vide, avec environ 160 px de blanc au-dessus.
                Centrer n'a de sens que dans une boite qui epouse son contenu.
                Dans une boite etiree, ca ne produit que du vide. */}
            <Box ref={refTexte} sx={{ margin: "0" }}>
            <EditableField
              /* PD-22 : les lignes « - » / « • » de Nawal deviennent des puces
                 (voir puces.js). Pas dans l'editeur : il edite le texte brut. */
              field={isEditor ? sansBlancsAutour(format?.text) : avecPuces(sansBlancsAutour(format?.text))}
              type="text"
              isEditor={isEditor}
              sx={ APERCU_EDITO ? {
                /* Hierarchie editoriale. Elle ne vient PAS d'une reecriture des
                   contenus : elle se deduit de la structure deja presente, le
                   premier bloc servant de chapo quand il est court. Mesure sur
                   le catalogue : 411 des 730 ecrans de texte en beneficient,
                   les 319 autres restent uniformes et corrects. */
                color:"#3A3A3A",
                fontSize:["15px", "16px", "16px", "17px"],
                lineHeight:"178%",
                textAlign:"left",
                "& p": { margin:"0 0 20px" },
                "& p:last-child": { marginBottom:0 },
                '& [data-chapo="oui"]': {
                  color:"text.black",
                  fontSize:["18px", "20px", "20px", "22px"],
                  lineHeight:"155%",
                  marginBottom:"24px",
                },
              } : {
                color:"text.black",
                /* ⚠️ CORPS DE LA CARTE RELU DANS FIGMA (21/09/2026, lot 3b).
                   Desktop 17 px (noeud 3:181383, `text-[17px]`), Mobile 15 px
                   (3:181469). Le code posait 20 et 14 : trop gros la ou la
                   planche resserre, trop petit la ou elle agrandit.
                   Interligne 150 % et non 140 : `leading-[1.5]` sur les TROIS
                   noeuds de texte lus (3:141175, 3:181383, 3:181469), et le
                   jeton « Text/Desktop/Body/Large » le confirme. Les 10 points
                   manquants tassaient chaque paragraphe. */
                /* ⛔ 08/10/2026 — ECART VOULU PAR NAWAL a la planche 06'
                   (3:181383, 17 px) : avec image aussi, le corps passe a 20 px
                   en Desktop et Tablet, comme le texte seul. Interligne 150 %,
                   soit 30 px, le meme que le texte seul. Mobile inchange (15). */
                fontSize:["15px", "20px", "20px", "20px"],
                lineHeight:"150%",
                /* ⛔ LE TEXTE SEUL N'EST PAS LE TEXTE ILLUSTRE (comparateur, 02/10).
                   Les 17 px ci-dessus viennent de la planche 06' (avec image,
                   3:181383). La planche 06, SANS image, pose 20 px / 30 en
                   Desktop et Tablet (3:141175, 3:141232), 15 / 21 en Mobile
                   (3:141245), et 25 px entre paragraphes (espacement de 25 du
                   cadre 3:141174). */
                ...(commeIllustre ? {} : {
                  fontSize:["15px", "20px", "20px", "20px"],
                  lineHeight:["21px", "30px", "30px", "30px"],
                  /* 25 px a l'oeil entre paragraphes : 25 - 9,2 - 7,8 = 8 (Mobile
                     25 - 5,9 - 5,1 = 14), voir la note de la carte. */
                  "& p + p": { marginTop:["14px", "8px", "8px", "8px"] },
                }),
                /* ⛔ PD-22 (03/10/2026) — LA PUCE DE LA PLANCHE 06 : un point noir,
                   le texte en retrait de 30 px (le noeud 3:141175 part a 482,
                   puce comprise, et son texte a 512 sur l'image de la planche),
                   22 px en Mobile (3:141245), et entre deux puces l'ecart des
                   paragraphes. Une
                   liste touchee par du texte s'en ecarte autant. */
                '& ul[data-puces]': {
                  margin:0,
                  paddingLeft:["22px", "30px", "30px", "30px"],
                  listStyle:"disc",
                  whiteSpace:"normal",
                },
                '& ul[data-puces] > li + li, & ul[data-puces][data-avant]': { marginTop:["14px", "8px", "8px", "8px"] },
                '& ul[data-puces][data-apres]': { marginBottom:["14px", "8px", "8px", "8px"] },
                // L65 : le texte long est ferre a gauche sur la maquette. Centre,
                // un paragraphe de plusieurs lignes devient penible a lire.
                textAlign:"left",
              }}
            />
            </Box>
            {fonduVisible && <FonduBas suite={suite} couleur={FOND_ECRAN_CONTENU} />}
          </Box>
          </Box>
        </BlocIllustre>
        </>)}
      </CenterContainer>
      {/* ⛔ LE CTA SE CALE SUR LA COLONNE, PAS SUR LE BORD (21/09/2026, lot D).
          Sur les ecrans mesures, la rangee de controles est une colonne de 750 px
          centree et le CTA se pose sur SON bord droit (x=1131 sur 1512). Ce format
          ne la demandait pas : son bouton partait au bord droit de l'ecran, mesure
          a 1857 sur 1916, alors que la planche 06 le place vers 1100 sur 1512.
          ⚠️ `colonne` reste passe FORMAT PAR FORMAT : l'imposer a BottomBar
          ramenerait vers le centre les boutons du Diagnostic et de la video, que
          la planche pose contre le bord. */}
      <BottomBar
        /* ⛔ 01/10/2026 — POINT 1 D'HICHAM : sur un ecran ILLUSTRE la barre se
           range dans la moitie de contenu, comme le font deja le quiz et le
           champ de saisie. Voir la note de `BottomBar`. */
        demiDroite={aUneImage}
        reperes={reperesClavier}
        colonne={aUneImage ? undefined : LARGEUR_CARTES}   /* ⚠️ `colonne` attend une LARGEUR, pas un booleen : passe a `true`, il arrivait dans `maxWidth` et etait ignore. */
        actFormation={actFormation}
        actIndex={actIndex}
        onlyContent={isEditor ? false : onlyContent}
        isEditor={isEditor}
        /* Lot G — mesure au pixel sur les frames : l'ecran de texte SANS image
           (06 LONG TEXT) porte une bande teintee MINCE de 111 px, qui ne contient
           que l'aide clavier et le CTA. L'ecran de texte AVEC image
           (06' LONG TEXT + IMAGE) n'a AUCUNE bande : il reste blanc jusqu'en bas.
           Les deux compositions different donc reellement — d'ou la condition. */
        /* ⚠️ `format?.image` ET NON `commeIllustre`, malgre l'uniformite apparente.
           `commeIllustre` vaut aussi vrai pour un « panneau », c'est-a-dire un
           texte SANS image vu en apercu editorial : avec lui, la bande de 111 px
           disparaissait dans le mode ou le design est justement relu, alors que
           la planche la montre. Ce qui decide de la bande, c'est la presence
           d'une image, pas le mode d'affichage. */
        fond={format?.image ? "transparent" : FOND_BANDE_BASSE}
        /* 03/10/2026 : la bande du texte long fait 110 en Tablet et 100 en
           Mobile, bouton a 30 et 35 du bas (3:141213, 3:141248) ; le code
           posait 111 et 24 / 20 partout, et le bouton tombait 7 et 26 px trop
           bas. */
        hauteur={format?.image ? undefined : ["100px", "110px", "111px", "111px"]}
        bas={format?.image ? undefined : [35, 30, 30, 30]}
        buttons={
          <EditableButton
            isEditor={false}
            type="button"
            /* ⛔ POINT 1.3 DE LA FICHE V2. Le libelle etait celui saisi
               dans le contenu, d'ou les « C'est parti ! » et « On
               continue ? » qui trainaient depuis des annees. Il est
               desormais CALCULE, et la regle ecrase le contenu :
               ecran de lecture : rien n'est enregistre.
               Le champ de saisie libre est retire avec lui
               (isEditor={false}) : tant qu'il existait, le libelle
               rederivait au module suivant. */
            field={languageValues?.next?.[actLanguage]}
            sx={{}}
            onClick={continuer}
          />
        }
      />
    </Box>
  )
}


export default FormatTexte
